import type { ClassificationSchema, ClassificationVocabulary, FlagLanding } from './api';

// <purpose>Session-scoped read-through cache for the tenant's classification vocabulary/schema.
// getConfig() (api.ts — THE fetch seam) writes here on every successful /config resolution;
// App.tsx reads through it once at boot for an instant first paint, before the real fetch settles.
// Deliberately narrower than the full AppConfig: only the fields the loading-posture skeleton and
// ClassificationSchemaContext need to paint immediately — role, schema, vocabulary, row layout.</purpose>
// <dependencies>sessionStorage only. Imports AppConfig's schema/vocabulary TYPES from ./api
// (erased at compile time — no runtime coupling back to api.ts, which is what imports THIS
// module's functions, so no circular require).</dependencies>
// <constraints>
//   The membership line this module actually draws is capability vs. display, not "security-
//   adjacent vs. not": capability/security flags (formExtractionEnabled, simulateSystemData,
//   uiShowSourceChannel, uiWarps) are NEVER cached here and NEVER read from this module, because
//   they GATE — a stale entry, or the unauthenticated Anmeldung pre-warm response, could flip a
//   capability on before the real auth check runs. DISPLAY preferences (uiRowLayout,
//   uiAttachmentsCountInline) ARE cached, because a stale one can only mis-PAINT — it decides how
//   something already-visible is drawn on a warm load's first frame, never whether it renders at
//   all — and self-heals within the same round-trip getConfig() always makes regardless of cache
//   state.
//   sessionStorage, not localStorage: this is per-tenant classification vocabulary (category/
//   marktprozess label TEXT), not ticket PII — but it IS tenant-identifying config, so a shared/
//   kiosk machine must not carry one tenant's category vocabulary into a future browser session
//   after the tab closes. The cache's lifetime is bounded to the tab's, same as the auth session
//   it describes.
//   Every write is a FULL replace of the single slot, never a partial merge — see
//   writeConfigCache — so a role/vocabulary change can never leave a stale field from the
//   previous entry paired with the new key.
//   A same-tab tenant switch (re-login as a different customer without closing the tab) is a
//   staleness window this module cannot close structurally: role isn't knowable before the fetch
//   that would reveal it changed, so a stale read-through can briefly serve the PRIOR tenant's
//   vocabulary. It self-heals within one round-trip — getConfig()'s own fetch is always
//   authoritative and always re-runs regardless of cache state (see api.ts) — never a permanent
//   cross-tenant leak, and never ticket data, only category label text.
// </constraints>

const CACHE_KEY = 'smarttix_vocab_cache';

export interface CachedVocabConfig {
  role: string | null;
  classificationSchema: ClassificationSchema | null;
  classificationVocabulary: ClassificationVocabulary | null;
  // Mirrors AppConfig.uiRowLayout's own inline union (api.ts) rather than importing RowLayout
  // from the context layer — services/ must not depend on components/context/.
  uiRowLayout?: 'strip_label' | 'strip_abbr';
  // A DISPLAY preference, cached for the same reason uiRowLayout is: it decides what a warm load
  // paints before the fetch settles, and painting the wrong one shifts content when the real value
  // lands.
  uiAttachmentsCountInline?: boolean;
  // The kinds a clerk can approve on this tenant (core, part 4b) — a DISPLAY fact, cached for the
  // same reason: it decides how row 3 is drawn on a warm load's first frame, never whether
  // something renders at all.
  decisionKinds?: string[];
  // The served claim-kind -> Bearbeitung handling map (core, part 4c) — same DISPLAY-fact class as
  // decisionKinds directly above, cached for the same warm-first-paint reason. `{}` when the
  // tenant's role declares no handling map (gmsb/wmsb/vnb today).
  decisionKindHandling?: Record<string, string>;
  // The served flag landings (core generation 15; part 7 fix loop, 2026-10-12) — the same DISPLAY
  // class: it decides whether a flag pill the band already implies is drawn on a warm first frame.
  flagLandings?: Record<string, FlagLanding>;
}

interface CacheEnvelope { key: string; config: CachedVocabConfig; }

// djb2 — not cryptographic, doesn't need to be: this is a change-detector for cache-key purposes
// only (see computeVocabCacheKey), not a security boundary. Deterministic across calls in the
// same tab is the only property that matters here.
function stableHash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// The served AppConfig carries no explicit schema-version field (neither ClassificationSchema nor
// ClassificationVocabulary declares one) — the hash of the schema+vocabulary payload IS the
// version proxy: any label/id edit on the tenant's taxonomy changes the hash exactly like a
// version bump would, without inventing a field the backend doesn't send. role is folded in
// separately (not just through the hash) so the key stays legible in devtools ("vnb:1a2b3c")
// rather than being one opaque blob.
export function computeVocabCacheKey(cfg: Pick<CachedVocabConfig, 'role' | 'classificationSchema' | 'classificationVocabulary'>): string {
  const payload = JSON.stringify({ s: cfg.classificationSchema ?? null, v: cfg.classificationVocabulary ?? null });
  return `${cfg.role ?? 'null'}:${stableHash(payload)}`;
}

// Read-through: served for App.tsx's instant first paint, BEFORE the real fetch resolves. Not
// validated against a "current" key — there is no such thing pre-fetch, since role itself comes
// FROM the fetch — but IS validated for internal self-consistency: if the stored key no longer
// matches a fresh hash of the stored payload (corrupt write, manual edit, or a superseded
// key-derivation after a code change), the entry is untrustworthy and is dropped rather than
// served, clearing the slot so a later read doesn't hit the same corrupt entry again.
export function readCachedVocabConfig(): CachedVocabConfig | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const envelope = JSON.parse(raw) as Partial<CacheEnvelope>;
    if (!envelope?.config || !envelope.key) return null;
    if (envelope.key !== computeVocabCacheKey(envelope.config)) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return envelope.config;
  } catch {
    return null;
  }
}

// Whether a cached entry is COMPLETE enough for App.tsx to seed its `configSettled` flag TRUE from
// it (final review, item #10). A cache written by a pre-part-4b bundle predates `decisionKinds`
// ENTIRELY — the key is ABSENT, never an empty array: every write since Task 7 sets it explicitly,
// even to `[]` for a role with no draft posture — so `decisionKinds !== undefined` is exactly the
// "this cache carries every field a config-dependent surface might read" test. Seeding settled=true
// from an incomplete entry would let the first paint after a deploy read `cm.statesApprovedKind` as
// false (the handling map floors to `{}` the same way a genuine cold load does) and paint the
// date row grammar — never the badge — on a tenant whose served map states a Bearbeitung, then
// swap to the real grammar a moment later once /config lands. A CONTENT SWAP on first paint is
// exactly what the shimmer posture (UI Layout Stability) exists to prevent.
//
// Chosen over folding `decisionKinds` into computeVocabCacheKey's hash (the other option this
// finding named): that hash is deliberately scoped to schema/vocabulary/role — the fields whose
// CONTENT changing means the cached WORDS are wrong (see the module's own <constraints> above) —
// and decisionKinds is a DISPLAY fact in that same class as rowLayout/uiAttachmentsCountInline,
// neither of which is in the hash either. Folding it in would also bust every entry ever written
// (the hash's shape itself would change, not just entries missing the field), which is a much
// blunter invalidation than the one this defect actually needs — a targeted field-presence check
// at the one read site that cares (App.tsx's configSettled seed) reaches exactly the pre-part-4b
// entries without touching the versioning contract every other cached field already relies on.
//
// `decisionKindHandling` (part 4c) joins the same check for the same reason: a cache written
// before this field existed predates it ENTIRELY too, and seeding settled=true from it would let
// a warm first paint read the tenant as owning no claim-kind -> Bearbeitung map before /config has
// actually said so. `flagLandings` (part 7 fix loop, 2026-10-12) joins it for the same reason: a
// pre-field cache would paint an implied flag pill and then remove it when /config lands.
export function cacheIsSettled(cached: CachedVocabConfig | null): boolean {
  return cached !== null && cached.decisionKinds !== undefined && cached.decisionKindHandling !== undefined
    && cached.flagLandings !== undefined;
}

// Write-through on every successful /config fetch (getConfig, api.ts) — ALWAYS a full replace of
// the single slot (JSON.stringify of a freshly-built envelope, never a mutate-in-place of the old
// one) so a role/schema change can never leave a stale field paired with the new key. try/catch:
// private-mode storage quota is a real, observed failure mode elsewhere in this app
// (FirstRunTooltip) — this cache is an optimization, never a dependency, so a write failure is
// silent.
export function writeConfigCache(config: CachedVocabConfig): void {
  try {
    const envelope: CacheEnvelope = { key: computeVocabCacheKey(config), config };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(envelope));
  } catch { /* private-mode quota — cache is best-effort */ }
}
